import { css } from '@emotion/react';
import { TextSubtitle2RBMixin, WidgetLayout } from '@mobalytics/shared';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { media } from '../../../../ui/global/media-query';
import { PricePerTimeframe } from '../../../payment/payment-details/plan-details/price-per-timeframe/price-per-timeframe.component';
import { getMoneyBaseUnit } from '../../../payment/utils/get-money-base-unit.utils';
import { ProductI } from '../../payment.types';
import { BundleInfo } from './bundle-info/bundle-info.component';
import { CheckoutPrice } from './checkout-price/checkout-price.component';

interface Props {
  className?: string;
  product: ProductI;
  isBundleAdded: boolean;
  onBundleToggle: (isBundleAdded: boolean) => void;
}

export const CheckoutDetails: React.FC<Props> = (props) => {
  const { className, product, isBundleAdded, onBundleToggle } = props;
  const { t } = useTranslation();
  const totalPrice =
    product.__typename === 'TreasuryProductBattlePass'
      ? product.price.basePrice.amount + (isBundleAdded ? product.bundle?.finalPrice.amount || 0 : 0)
      : product.price.basePrice.amount;

  return (
    <WidgetLayout className={className} css={wrapperCss} bodyOverrideCss={widgetLayoutBodyCss}>
      <div css={contentCss}>
        <div css={lineCss}>
          <p css={headerCss}>{t('Summary')}</p>
        </div>

        <PricePerTimeframe
          currency={product.price.basePrice.currency}
          price={getMoneyBaseUnit(product.price.basePrice.amount, true)}
          timeframe={'once'}
          css={pricePerTimeframeCss}
        />
        {product.__typename === 'TreasuryProductBattlePass' && product.bundle && (
          <BundleInfo bundle={product.bundle} isChecked={isBundleAdded} onChange={onBundleToggle} />
        )}
        <div css={delimiterCss} />

        <CheckoutPrice
          label={product.name}
          priceAmount={product.price.basePrice.amount}
          priceCurrency={product.price.basePrice.currency}
          css={itemPriceCss}
        />
        <div css={delimiterCss} />
        {product.__typename === 'TreasuryProductBattlePass' && isBundleAdded && product.bundle && (
          <>
            <CheckoutPrice
              label={product.bundle.name}
              priceAmount={product.bundle.finalPrice.amount}
              priceCurrency={product.bundle.finalPrice.currency}
              css={itemPriceCss}
            />
            <div css={delimiterCss} />
          </>
        )}
        <CheckoutPrice
          label={t('Total')}
          priceAmount={totalPrice}
          priceCurrency={product.price.basePrice.currency}
          css={totalPriceCss}
        />
      </div>
    </WidgetLayout>
  );
};

const wrapperCss = css`
  flex: 1 1 0%;
  max-width: 400px;
  width: 100%;
  min-height: 545px;
  overflow: hidden;
  ${media.medium} {
    min-height: auto;
  }
`;

const pricePerTimeframeCss = css`
  margin-top: 16px;
  margin-bottom: 24px;
`;

const contentCss = css`
  position: relative;
`;

const delimiterCss = css`
  width: 100%;
  height: 1px;
  background: var(--general-grey-300);
  opacity: 0.3;
  margin: 16px 0;
`;

const widgetLayoutBodyCss = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  padding: 40px;
  flex: 1 1 auto;
  background-color: #252046;
  ${media.medium} {
    padding: 16px 12px;
  }
`;

const lineCss = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const itemPriceCss = css`
  color: var(--general-grey-100);
`;

const totalPriceCss = css`
  color: var(--general-white-100);
`;

const headerCss = css`
  ${TextSubtitle2RBMixin};
`;
